/**
 * "Who to call" on the new job page's first screen (Corbin 10/28: "a job page
 * needs customer contact information ready and available so reaching out is
 * not difficult"). Never under More.
 *
 *   - The site contact (lets us in) and the billing contact (pays/approves),
 *     once if they're the same person. Before the visit the site contact
 *     leads; after it, the billing contact.
 *   - Big Call / Text / Email. Call dials and logs the call on the job (then
 *     one tap says how it went); Text and Email open a short message to THAT
 *     person and send through the job's message tool (logged on the job).
 *   - No Text button without a yes to texts; a bounced email is red; a missing
 *     phone or email is fixable on the customer's Contacts tab.
 *   - The last contact on the job, in one line.
 *   - Gate and access ride with the site contact, before the visit.
 * Server: GET /jobs/:id/reach (services/jobPageReads.ts).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CALL_DOCK_ENABLED, openOutboundCall } from "@/lib/use-incoming-call";
import { InfoButton } from "@/components/info-button";
import type { JobDetail } from "../detail/types";
import { FRESH_HELP } from "./fresh-help";
import type { Phase } from "./freshModel";

export interface ReachPerson {
    contactId: string;
    name: string;
    title: string | null;
    role: "site" | "billing" | "both" | "other";
    phone: string | null;
    email: string | null;
    emailBounced: { at: string; reason: string | null } | null;
    textOk: boolean;
    textNote: string | null;
    best: "call" | "email" | null;
}

export interface JobReach {
    people: ReachPerson[];
    access: { notes: string | null; gateCodeOnFile: boolean };
    total: number;
    last: {
        how: "call" | "text" | "email";
        at: string;
        byName: string | null;
        toNumber: string | null;
        note: string | null;
    } | null;
}

const ROLE_WORDS: Record<ReachPerson["role"], string> = {
    site: "Site contact",
    billing: "Billing",
    both: "Site + billing",
    other: "Contact",
};

const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Chicago",
    });

/** "Darcy called Tue 2:10 PM — Left voicemail". */
export function lastLine(last: JobReach["last"]): string {
    if (!last) return "Nobody has reached out on this job yet.";
    const who = (last.byName ?? "").split(" ")[0] || "Someone";
    const verb = last.how === "call" ? "called" : last.how === "text" ? "texted" : "emailed";
    const note = last.note ? ` — ${last.note.length > 60 ? `${last.note.slice(0, 57)}…` : last.note}` : "";
    return `${who} ${verb} ${when(last.at)}${note}`;
}

/** Who leads: the site contact before the visit, billing after. */
export function orderPeople(people: ReachPerson[], phase: Phase): ReachPerson[] {
    const rank = (p: ReachPerson) =>
        p.role === "both" ? 0 : phase === "before" ? (p.role === "site" ? 1 : 2) : p.role === "billing" ? 1 : 2;
    return [...people].sort((a, b) => rank(a) - rank(b)).slice(0, 2);
}

const BIG = "inline-flex min-h-[44px] items-center rounded-lg px-4 text-[15px] font-bold";

export function WhoToCall({ job, phase }: { job: JobDetail; phase: Phase }) {
    const qc = useQueryClient();
    const reachQ = useQuery({
        queryKey: ["job", job.id, "reach"],
        queryFn: async () => (await api.get<{ reach: JobReach }>(`/api/jobs/${job.id}/reach`)).reach,
        staleTime: 30_000,
    });
    const [compose, setCompose] = useState<{ person: ReachPerson; channel: "sms" | "email" } | null>(null);
    const [lastCallId, setLastCallId] = useState<string | null>(null);
    const refresh = () => qc.invalidateQueries({ queryKey: ["job", job.id, "reach"] });

    const logCall = useMutation({
        mutationFn: (p: ReachPerson) =>
            api.post<{ callLog: { id: string } }>("/api/call-logs", {
                jobId: job.id,
                customerId: job.customer_id,
                ...(job.site_id ? { siteId: job.site_id } : {}),
                direction: "outbound",
                calleeNumber: p.phone ?? undefined,
                notes: `Called ${p.name}`,
            }),
        onSuccess: (r) => {
            setLastCallId(r.callLog.id);
            refresh();
        },
        onError: () => toast.error("The call wasn't saved on the job. Log it from the classic page."),
    });
    const callOutcome = useMutation({
        mutationFn: (v: { id: string; notes: string }) => api.patch(`/api/call-logs/${v.id}`, { notes: v.notes }),
        onSuccess: () => {
            setLastCallId(null);
            refresh();
        },
        onError: () => toast.error("Couldn't save how the call went."),
    });

    const call = (p: ReachPerson) => {
        if (!p.phone) return;
        if (CALL_DOCK_ENABLED)
            openOutboundCall({ toNumber: p.phone, matchedCustomerId: job.customer_id, fromName: p.name });
        else window.location.href = `tel:${p.phone.replace(/[^\d+]/g, "")}`;
        logCall.mutate(p);
    };

    const people = orderPeople(reachQ.data?.people ?? [], phase);
    const contactsHref = `/customers/${job.customer_id}?tab=contacts`;

    return (
        <section
            className="rounded-2xl border border-border bg-card px-4 py-3 text-card-foreground"
            aria-label="Who to call"
            data-testid="fresh-who-to-call"
        >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="m-0 text-[15px]" data-testid="fresh-last-contact">
                    <b>Last:</b> {reachQ.isLoading ? "…" : lastLine(reachQ.data?.last ?? null)}
                </p>
                <span className="flex items-center gap-3">
                    <Link to={contactsHref} className="text-[14px] font-semibold text-primary underline">
                        All contacts{reachQ.data ? ` · ${reachQ.data.total}` : ""}
                    </Link>
                    <InfoButton topic={FRESH_HELP.contact} />
                </span>
            </div>
            {lastCallId && (
                <div className="mb-2 flex flex-wrap items-center gap-2 text-[14px]" data-testid="fresh-call-outcome">
                    <span className="font-semibold">How did it go?</span>
                    {["Talked to them", "Left voicemail", "No answer"].map((w) => (
                        <button
                            key={w}
                            type="button"
                            className="min-h-[36px] rounded-lg border border-border px-3 hover:bg-muted"
                            onClick={() => callOutcome.mutate({ id: lastCallId, notes: w })}
                        >
                            {w}
                        </button>
                    ))}
                </div>
            )}
            {!reachQ.isLoading && people.length === 0 && (
                <p className="m-0 text-[15px] text-amber-800 dark:text-amber-300">
                    No contact on file for this job.{" "}
                    <Link to={contactsHref} className="font-semibold underline">
                        Add one
                    </Link>
                </p>
            )}
            <div className="grid gap-2 md:grid-cols-2">
                {people.map((p) => (
                    <div
                        key={p.contactId}
                        className="grid gap-1 rounded-xl bg-muted/60 px-3 py-2.5"
                        data-testid={`fresh-person-${p.role}`}
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-[17px] font-bold">{p.name}</span>
                            <span className="text-[12px] font-bold uppercase tracking-wide text-primary">
                                {ROLE_WORDS[p.role]}
                            </span>
                        </div>
                        {p.title && <span className="text-[14px] text-muted-foreground">{p.title}</span>}
                        <span className="text-[14px] text-muted-foreground">
                            {p.phone ? (
                                <span className="font-mono">{p.phone}</span>
                            ) : (
                                <Link to={contactsHref} className="font-semibold text-primary underline">
                                    Add phone
                                </Link>
                            )}
                            {" · "}
                            {p.emailBounced ? (
                                <>
                                    <span className="font-bold text-red-700 dark:text-red-400">
                                        Email bounced{" "}
                                        {new Date(p.emailBounced.at).toLocaleDateString("en-US", {
                                            month: "numeric",
                                            day: "numeric",
                                        })}
                                    </span>{" "}
                                    <Link to={contactsHref} className="font-semibold text-primary underline">
                                        Fix email
                                    </Link>
                                </>
                            ) : p.email ? (
                                <span className="break-all">{p.email}</span>
                            ) : (
                                <Link to={contactsHref} className="font-semibold text-primary underline">
                                    Add email
                                </Link>
                            )}
                        </span>
                        {p.best && (
                            <span className="text-[13px] text-muted-foreground">
                                Best way: {p.best === "call" ? "a call" : "email"}
                            </span>
                        )}
                        {phase === "before" && (p.role === "site" || p.role === "both") && reachQ.data && (
                            <AccessLine access={reachQ.data.access} />
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            {p.phone && (
                                <button
                                    type="button"
                                    className={cn(BIG, "bg-primary text-primary-foreground hover:bg-primary/90")}
                                    onClick={() => call(p)}
                                >
                                    Call
                                </button>
                            )}
                            {p.phone &&
                                (p.textOk ? (
                                    <button
                                        type="button"
                                        className={cn(BIG, "border border-border bg-card hover:bg-muted")}
                                        onClick={() => setCompose({ person: p, channel: "sms" })}
                                    >
                                        Text
                                    </button>
                                ) : (
                                    <span className="text-[13px] text-muted-foreground" data-testid="fresh-no-text">
                                        No texts: {p.textNote}
                                    </span>
                                ))}
                            {p.email && !p.emailBounced && (
                                <button
                                    type="button"
                                    className={cn(BIG, "border border-border bg-card hover:bg-muted")}
                                    onClick={() => setCompose({ person: p, channel: "email" })}
                                >
                                    Email
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {compose && (
                <Composer
                    job={job}
                    person={compose.person}
                    channel={compose.channel}
                    onDone={() => {
                        setCompose(null);
                        refresh();
                    }}
                />
            )}
        </section>
    );
}

function AccessLine({ access }: { access: JobReach["access"] }) {
    const bits = [access.gateCodeOnFile ? "Gate code on file" : null, access.notes].filter(Boolean);
    if (bits.length === 0) return null;
    return <span className="text-[14px]">🔑 {bits.join(" · ")}</span>;
}

function Composer({
    job,
    person,
    channel,
    onDone,
}: {
    job: JobDetail;
    person: ReachPerson;
    channel: "sms" | "email";
    onDone: () => void;
}) {
    const first = person.name.split(" ")[0] ?? "";
    const [body, setBody] = useState(`Hi ${first}, `);
    const [subject, setSubject] = useState(
        `About your job${job.display_number ? ` #${job.display_number}` : ""}${job.site_name ? ` at ${job.site_name}` : ""}`,
    );
    const send = useMutation({
        mutationFn: () =>
            api.post<{ skipped?: string | null }>(`/api/jobs/${job.id}/message-customer`, {
                channel,
                body: body.trim(),
                to: channel === "sms" ? person.phone : person.email,
                ...(channel === "email" && subject.trim() ? { subject: subject.trim() } : {}),
            }),
        onSuccess: (r) => {
            if (r?.skipped === "opted_out") toast.warning(`Not sent: ${first} has opted out of texts.`);
            else toast.success(channel === "sms" ? `Text sent to ${first}` : `Email sent to ${first}`);
            onDone();
        },
        onError: (e: unknown) => {
            const b = (e as { body?: { message?: string } } | null)?.body;
            toast.error(b?.message ?? "It didn't send. Try again, or use the classic page.");
        },
    });
    return (
        <div className="mt-3 grid gap-2 rounded-xl border border-border p-3" data-testid="fresh-composer">
            <p className="m-0 text-[14px] font-semibold">
                {channel === "sms" ? "Text" : "Email"} {person.name} ·{" "}
                <span className="font-normal text-muted-foreground">
                    {channel === "sms" ? person.phone : person.email}
                </span>
            </p>
            {channel === "email" && (
                <input
                    aria-label="Subject"
                    className="rounded-md border border-input bg-background px-3 py-2 text-[15px]"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                />
            )}
            <textarea
                aria-label="Message"
                className="min-h-[90px] rounded-md border border-input bg-background px-3 py-2 text-[15px]"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                autoFocus
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    className={cn(
                        BIG,
                        "bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground",
                    )}
                    disabled={send.isPending || body.trim().length < 2}
                    onClick={() => send.mutate()}
                >
                    Send
                </button>
                <button type="button" className={cn(BIG, "hover:bg-muted")} onClick={onDone}>
                    Cancel
                </button>
                <span className="text-[13px] text-muted-foreground">Saved on the job.</span>
            </div>
        </div>
    );
}
